import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListingCardListing, ListingCardSize } from './types';
import { useSameLine, useClampOverflow } from './useCardOverflow';
import {
  buildPropertyChips,
  buildCardOpenProps,
  buildCardStateClass,
  connectHintText,
  sizeDisplay,
  roomsDisplay,
} from './cardUtils';
import { formatAddressLine } from '../utils/addressFormatting';
import { TipBuyerButton, TipBuyerMenu, TipPreviewModal, useTipBuyer } from '../common/TipBuyer';
import VerifiedChip from '../common/VerifiedShieldBadge/VerifiedChip';
import buyerCoin from '../common/icons/coin.png';
import chatBubbleIcon from '../common/icons/chat-bubble.svg';
import { buyerRoomsFloor } from './buyerRoomsFloor';
import { shareUrlWithFallback } from '../../utils/shareUtils';
import { listingShareUrl } from '../utils/listingSlug';
import './BuyerCard.scss';

export interface BuyerCardProps {
  listing: ListingCardListing;
  /** 'big' (teal header) | 'medium' (inline coin) | 'small'. Defaults to 'big'. */
  size?: ListingCardSize;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
  isLiked?: boolean;
  onToggleLike?: (listing: ListingCardListing) => void;
  /** Tapping the card body opens it (parity with the seller card). */
  onOpen?: (listing: ListingCardListing) => void;
  /** Highlighted/selected card — dark inset ring + lift. */
  selected?: boolean;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Owner "..." manage menu, rendered as the first child when in profile manage mode. */
  manageNode?: React.ReactNode;
  /** Hide the Share + Suggest footer (e.g. inside an active conversation). */
  hideFooterActions?: boolean;
  /** Override the size headline text verbatim (e.g. an "55–70 m²" range or a
   *  pre-formatted label). When unset, falls back to `property_size` + "+m²". */
  sizeLabel?: string;
  /** Pre-formatted budget line rendered in the seller card's price slot (e.g.
   *  "≤ 800 000 €"). Area demand cards derive it from the interest's price
   *  criteria; per-building buyer cards from the listing's own budget. Omitted
   *  → no price row, for a buyer who set no budget. */
  priceLabel?: string;
  /** Override the desired-feature chips. When unset, they're derived from the
   *  listing's property_* fields via buildPropertyChips. */
  chips?: string[];
}

/**
 * The buyer's wanted size. The could-buy form asks for a range and the listing
 * keeps both bounds, either of which may stand alone. A max-only buyer has to
 * read "≤ 80 m²" — rendering it as "80+ m²" would advertise the opposite of
 * what they asked for. A node rather than a string so the lower-bound case
 * keeps its lighter-weight "+".
 */
const SizeStat = ({ listing }: { listing: ListingCardListing }) => {
  const size = sizeDisplay(listing);
  if (size.kind === 'range') {
    return (
      <>
        {size.min}–{size.max} m²
      </>
    );
  }
  if (size.kind === 'min') {
    return (
      <>
        {size.min}
        <span className="buyer-card__plus">+</span>m²
      </>
    );
  }
  if (size.kind === 'max') return <>≤ {size.max} m²</>;
  return null;
};

/**
 * Buyer-interest card for the Building view's Buyers carousel: coin-left layout
 * with the buyer's looking-for size/rooms, description, desired-feature chips,
 * and a Share + Suggest footer (Suggest reuses the tip-buyer flow).
 *
 * `property_rooms` is always a buyer minimum here (rooms_min or its mirror), so
 * it renders as "N+ rooms" (see buyerRoomsFloor); a bare "3 rooms" read as an
 * exact count and looked wrong under the Explore rooms filter (JAA-418).
 */
const BuyerCard: React.FC<BuyerCardProps> = ({
  listing,
  size = 'big',
  isLoggedIn,
  setLoginModalVisible,
  onOpen,
  selected = false,
  className,
  onMouseEnter,
  onMouseLeave,
  manageNode,
  hideFooterActions = false,
  sizeLabel,
  priceLabel,
  chips: chipsOverride,
}) => {
  const { t } = useTranslation();

  const isOwnListing = !!listing.isUserOwnEvent;
  const stateClass = buildCardStateClass('buyer-card', selected, isOwnListing);

  const tipBuyer = useTipBuyer({
    buyWillingnessId: listing._id,
    isLoggedIn,
    setLoginModalVisible,
  });
  const canSuggest = !listing.isUserOwnEvent;

  const chips = chipsOverride ?? buildPropertyChips(listing, t, { includeElevator: true });

  const addressLine = formatAddressLine(listing.address);

  const hasSize =
    !!sizeLabel || !!listing.property_size || !!listing.property_size_max;
  const roomCriteria = roomsDisplay(listing);
  const roomFloor = roomCriteria.rooms != null
    ? buyerRoomsFloor(roomCriteria.rooms, roomCriteria.flexible)
    : NaN;
  const hasRooms = Number.isFinite(roomFloor) && roomFloor >= 1;
  const hasStats = hasSize || hasRooms;

  // Interactive card is a listing's final level — nothing may be hidden with no
  // way back. Stats ride one line with a dot when they fit (else wrap, dot off);
  // a long description gets a Show more/less toggle; extra tags fold into "+N".
  const [statsRef, statsInline] = useSameLine<HTMLDivElement>(
    '.buyer-card__stat',
    `${sizeLabel}|${listing.property_size}|${listing.property_size_max}|${roomCriteria.rooms}|${roomCriteria.flexible}`,
  );
  const showStatDot = statsInline && hasSize && hasRooms;

  const [descExpanded, setDescExpanded] = useState(false);
  const [descRef, descOverflows] = useClampOverflow<HTMLParagraphElement>(
    listing.property_description,
    descExpanded,
  );

  // Share the single-listing view (like the seller cards), via the backend
  // share URL that serves an OG preview and redirects to /listing/:id.
  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await shareUrlWithFallback({ url: listingShareUrl(listing._id), t });
  };

  // Description spans full width below the head. When absent it's omitted and the
  // card shrinks (content-driven height); a card with no detail at all shows the
  // connect nudge under the address instead (rendered in the head-text column).
  const descriptionEl = listing.property_description ? (
    <div className="buyer-card__desc">
      <p
        ref={descRef}
        className={`buyer-card__description${
          descExpanded ? ' buyer-card__description--full' : ''
        }`}
      >
        {listing.property_description}
      </p>
      {descOverflows && (
        <button
          type="button"
          className="buyer-card__more-link"
          onClick={(e) => {
            e.stopPropagation();
            setDescExpanded((v) => !v);
          }}
        >
          {descExpanded ? t('common.showLess', 'Show less') : t('common.showMore', 'Show more')}
        </button>
      )}
    </div>
  ) : null;

  // All tags shown, wrapping onto as many rows as needed (no fold/expander).
  const chipsEl =
    listing.verified || chips.length > 0 ? (
      <div className="buyer-card__chips">
        {listing.verified && <VerifiedChip type="buyer" />}
        {chips.map((chip) => (
          <span className="buyer-card__chip" key={chip}>
            {chip}
          </span>
        ))}
      </div>
    ) : null;

  // A buyer interest with nothing but a location (no stats, description or chips)
  // would otherwise render an empty body — fill it with a connect nudge so the
  // card invites action instead of looking broken.
  const hasDetail = hasStats || !!listing.property_description || chips.length > 0;
  const connectHintEl = hasDetail ? null : (
    <p className="buyer-card__connect-hint">
      {connectHintText(t, true, isOwnListing)}
    </p>
  );

  // JAA-355: badges are viewer-specific; a no-reload logout flip (token expiry, logout in flight) must not keep them live.
  const showOpenChat = !!listing.conversation_id && isLoggedIn !== false;

  const footer = (
    <div className="buyer-card__footer" onClick={(e) => e.stopPropagation()}>
      {priceLabel && <span className="buyer-card__price">{priceLabel}</span>}
      {!hideFooterActions && (
        <button type="button" className="buyer-card__share" onClick={handleShare}>
          {t('listingCard.share', 'Share')}
        </button>
      )}
      {!hideFooterActions && canSuggest && showOpenChat && (
        <button
          type="button"
          className="buyer-card__open-chat"
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(
              new CustomEvent('jaakko:open-conversation', {
                detail: { conversationId: listing.conversation_id },
              }),
            );
          }}
        >
          <img className="buyer-card__open-chat-icon" src={chatBubbleIcon} alt="" aria-hidden="true" />
          {t('listingCard.openChat', 'Open chat')}
        </button>
      )}
      {!hideFooterActions && canSuggest && !showOpenChat && (
        <div className="buyer-card__suggest-wrap">
          <TipBuyerButton
            onClick={tipBuyer.handleButtonClick}
            buttonRef={tipBuyer.buttonRef}
            className="tip-buyer-button--buyer-card"
          />
          <TipBuyerMenu
            isOpen={tipBuyer.showMenu}
            menuRef={tipBuyer.menuRef}
            buttonRef={tipBuyer.buttonRef}
            isLoggedIn={isLoggedIn}
            isLoadingListings={tipBuyer.isLoadingListings}
            dailyLimitReached={tipBuyer.dailyLimitReached}
            userSellListings={tipBuyer.userSellListings}
            tippedListings={tipBuyer.tippedListings}
            onSelectListing={tipBuyer.handleSelectListing}
            onLoginClick={() => {
              tipBuyer.closeMenu();
              setLoginModalVisible?.(true);
            }}
          />
        </div>
      )}
    </div>
  );

  const tipModals = (
    <TipPreviewModal
      isOpen={tipBuyer.showPreviewModal}
      selectedListing={tipBuyer.selectedListing}
      isSendingTip={tipBuyer.isSendingTip}
      tipSentSuccess={tipBuyer.tipSentSuccess}
      onConfirm={tipBuyer.handleConfirmTip}
      onClose={tipBuyer.closePreviewModal}
    />
  );

  const sizeClass = size === 'small' ? ' buyer-card--small' : '';
  const openProps = buildCardOpenProps(listing, onOpen);
  return (
    <div
      className={`buyer-card buyer-card--coin-left${sizeClass}${onOpen ? ' buyer-card--clickable' : ''}${stateClass}${
        className ? ` ${className}` : ''
      }`}
      {...openProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {manageNode}
      <div className="buyer-card__content">
        <div className="buyer-card__info">
          <div className="buyer-card__head-desc">
            <div className="buyer-card__head">
              <span className="buyer-card__coin-wrap" aria-hidden="true">
                <img className="buyer-card__coin-inline" src={buyerCoin} alt="" />
              </span>
              <div className="buyer-card__head-text">
                {hasStats && (
                  <div className="buyer-card__stats" ref={statsRef}>
                    {hasSize && (
                      <span
                        className={`buyer-card__stat${
                          showStatDot ? ' buyer-card__stat--sep' : ''
                        }`}
                      >
                        {sizeLabel ?? <SizeStat listing={listing} />}
                      </span>
                    )}
                    {hasRooms && (
                      <span
                        className="buyer-card__stat"
                        title={
                          roomCriteria.flexible
                            ? t('newLandingPage.couldBuy.main.roomsFlexible')
                            : undefined
                        }
                      >
                        {t('newLandingPage.couldBuy.myAreas.roomsValue', {
                          count: roomFloor,
                        })}
                      </span>
                    )}
                  </div>
                )}
                <p className="buyer-card__address">{addressLine}</p>
                {connectHintEl}
              </div>
            </div>
            {descriptionEl}
          </div>
          {chipsEl}
        </div>
        {(!hideFooterActions || !!priceLabel) && footer}
      </div>
      {tipModals}
    </div>
  );
};

export default BuyerCard;
